import BaseTypesenseSearch from '../typesense-search';
import { configure } from 'instantsearch.js/es/widgets';
import { connectSearchBox } from 'instantsearch.js/es/connectors';
import {
  getHits,
} from '../common-widgets';
import { TemplateParams, Widget } from 'instantsearch.js';

interface ISuggestion {
  count: number;
  q: string;
  collection?: string;
}

export default class SearchInput extends BaseTypesenseSearch {
  private resultHitsContainer: HTMLElement | string = '';
  private searchBoxContainer: HTMLElement | string = '';
  private isSearchBoxRendered = false;
  private MIN_QUERY_LENGTH = 0;

  constructor() {
    super();
  }

  getWidgets() {
    const hitsCssClasses = {
      root: 'root-hits-list',
      list: 'hits-list',
    };
    const hitsTemplate = {
      item(hit: ISuggestion, { html }: TemplateParams ) {
        return html`
            <a href="/recherche/${hit.q}/" target="_self">
              <span class="icon-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M12.0207 11.078L14.876 13.9327L13.9327 14.876L11.078 12.0207C10.0158 12.8721 8.69467 13.3353 7.33333 13.3333C4.02133 13.3333 1.33333 10.6453 1.33333 7.33333C1.33333 4.02133 4.02133 1.33333 7.33333 1.33333C10.6453 1.33333 13.3333 4.02133 13.3333 7.33333C13.3353 8.69467 12.8721 10.0158 12.0207 11.078ZM10.6833 10.5833C11.5294 9.71326 12.0019 8.54695 12 7.33333C12 4.75467 9.91133 2.66667 7.33333 2.66667C4.75467 2.66667 2.66667 4.75467 2.66667 7.33333C2.66667 9.91133 4.75467 12 7.33333 12C8.54695 12.0019 9.71326 11.5294 10.5833 10.6833L10.6833 10.5833Z" fill="currentColor"/>
                  </svg>
              </span>
              <span class="label">${hit.q}</span>
            </a>
        `;
      },
      empty(results: unknown, { html }: TemplateParams) {
        return html`<span></span>`;
      },
    };

    // Shared builder for search form elements (mutualisable with convention-collective)
    const buildSearchFormElements = (placeholder: string) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'ais-SearchBox';

      const form = document.createElement('form');
      form.setAttribute('role', 'search');
      form.classList.add('ais-SearchBox-form');
      form.noValidate = true;
      form.setAttribute('data-gtm-form-interact-id', '0');

      const input = document.createElement('input');
      input.className = 'ais-SearchBox-input';
      input.type = 'search';
      input.name = 'search';
      input.placeholder = placeholder;
      input.autocomplete = 'off';
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.spellcheck = false;
      input.maxLength = 512;
      input.setAttribute('aria-label', 'Recherche');
      input.setAttribute('data-gtm-form-interact-field-id', '0');

      const submit = document.createElement('button');
      submit.className = 'ais-SearchBox-submit';
      submit.type = 'submit';
      submit.title = 'Soumettre la recherche';

      const reset = document.createElement('button');
      reset.type = 'reset';
      reset.className = 'ais-SearchBox-reset';
      reset.title = 'Effacer la recherche';
      reset.hidden = true;

      const loading = document.createElement('span');
      loading.className = 'ais-SearchBox-loadingIndicator';
      loading.setAttribute('aria-hidden', 'true');
      loading.hidden = true;

      // attach SVGs using DOMParser (safer than innerHTML)
      const parser = new DOMParser();
      try {
        const svgDoc = parser.parseFromString(`<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                    <path d="M15.0258 13.8475L18.595 17.4158L17.4158 18.595L13.8475 15.0258C12.5198 16.0902 10.8683 16.6691 9.16667 16.6667C5.02667 16.6667 1.66667 13.3067 1.66667 9.16667C1.66667 5.02667 5.02667 1.66667 9.16667 1.66667C13.3067 1.66667 16.6667 5.02667 16.6667 9.16667C16.6691 10.8683 16.0902 12.5198 15.0258 13.8475ZM13.3542 13.2292C14.4118 12.1416 15.0024 10.6837 15 9.16667C15 5.94333 12.3892 3.33333 9.16667 3.33333C5.94333 3.33333 3.33333 5.94333 3.33333 9.16667C3.33333 12.3892 5.94333 15 9.16667 15C10.6837 15.0024 12.1416 14.4118 13.2292 13.3542L13.3542 13.2292Z" fill="white"/>
                </svg>`, 'image/svg+xml');
        const svgEl = svgDoc.documentElement as unknown as Node;
        submit.appendChild(svgEl);
      } catch (e) {
        submit.textContent = '';
      }

      // reset svg
      try {
        const resetDoc = parser.parseFromString('<svg viewBox="0 0 20 20" width="10" height="10" aria-hidden="true"><path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"/></svg>', 'image/svg+xml');
        const resetSvg = resetDoc.documentElement as unknown as Node;
        reset.appendChild(resetSvg);
      } catch (e) {
        reset.textContent = '';
      }

      // loading svg
      try {
        const loadingDoc = parser.parseFromString('<svg width="16" height="16" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg" stroke="#444" aria-hidden="true"><g fill="none" fill-rule="evenodd"><g transform="translate(1 1)" stroke-width="2"><circle stroke-opacity=".5" cx="18" cy="18" r="18" /><path d="M36 18c0-9.94-8.06-18-18-18"><animateTransform attributeName="transform" type="rotate" from="0 18 18" to="360 18 18" dur="1s" repeatCount="indefinite"/></path></g></g></svg>', 'image/svg+xml');
        const loadingSvg = loadingDoc.documentElement as unknown as Node;
        loading.appendChild(loadingSvg);
      } catch (e) {
        loading.textContent = '';
      }

      form.appendChild(input);
      form.appendChild(submit);
      form.appendChild(reset);
      form.appendChild(loading);
      wrapper.appendChild(form);

      return { wrapper, form, input, submit, reset, loading };
    };

    const getSearchBox = (container: HTMLElement | string, placeholder = 'Recherche...') => {
      const self = this;
      const node = typeof container === 'string' ? document.querySelector(container) as HTMLElement : container as HTMLElement;
      const customSearchBox = connectSearchBox((renderOptions: any, isFirstRender: boolean) => {
        const { query, refine, clear, isSearchStalled } = renderOptions;

        if (isFirstRender) {
          const { wrapper, form, input, reset } = buildSearchFormElements(placeholder);

          // Events
          input.addEventListener('input', (e) => {
            const val = (e.target as HTMLInputElement).value;
            refine(val);
          });

          form.addEventListener('submit', (e) => {
            e.preventDefault();
            const val = (input as HTMLInputElement).value;
            self.onQueryValidation(val);
          });

          reset.addEventListener('click', (e) => {
            e.preventDefault();
            clear();
            input.value = '';
          });

          input.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.code === 'Enter') {
              self.onQueryValidation((input as HTMLInputElement).value);
            }
          });

          node.appendChild(wrapper);
        }

        // update UI on each render
        const inputEl = node.querySelector('.ais-SearchBox-input') as HTMLInputElement | null;
        const resetEl = node.querySelector('.ais-SearchBox-reset') as HTMLButtonElement | null;
        const loadingEl = node.querySelector('.ais-SearchBox-loadingIndicator') as HTMLElement | null;

        if (inputEl) inputEl.value = query ?? '';
        if (resetEl) resetEl.hidden = !(query && query.trim().length > 0 && !isSearchStalled);
        if (loadingEl) loadingEl.hidden = !isSearchStalled;
      });

      return customSearchBox({ container: node, showReset: false,
        showLoadingIndicator: false });
    };

    return [
      this.resultHitsContainer ? getHits(<HTMLElement>this.resultHitsContainer, hitsCssClasses , hitsTemplate) : null,
      this.searchBoxContainer ? getSearchBox(this.searchBoxContainer) : null,
      this.configureParams ? configure(this.configureParams) : null,
    ].filter(Boolean) as Widget[];
  }

  onQueryValidation(query: string) {
    const trimmed = (query ?? '').trim();
    if (trimmed.length > this.MIN_QUERY_LENGTH) {
      window.location.href = `/recherche/${trimmed}`;
    }
  }

  listenToInput() {
    if (!this.isSearchBoxRendered) {
      const input = (this.searchBoxContainer as HTMLElement).querySelector('input');
      const button = (this.searchBoxContainer as HTMLElement).querySelector('button[type="submit"]');
      if (input && button) {
        input.addEventListener('keyup', (e: KeyboardEvent) => {
          if (e.code === 'Enter') {
            this.onQueryValidation(input.value);
          }
          // this.classList.toggle('show-suggestions', input.value.length > this.MIN_QUERY_LENGTH);
        });
        (<HTMLButtonElement>button).addEventListener('click', () => {
          this.onQueryValidation(input.value);
        });
        this.isSearchBoxRendered = true;
      }
    }
  }

  init() {
    this.resultHitsContainer = this.querySelector('.result-hits') as HTMLElement || '';
    this.searchBoxContainer = this.querySelector('.search-box-wrapper') as HTMLElement || '';

    this.searchInstance?.start();
    this.searchInstance?.addWidgets(this.getWidgets());
    this.searchInstance?.on('render', () => {
      // Les widgets ont été rendus
      this.listenToInput();
    });
  }

  connectedCallback() {
    setTimeout(() => {
      this.noHitsOnEmptyQuery = true;
      super.initSearchInstance();
      this.init();
    });
  }
}
